import { showMessageWithTimeout } from "../actions/app";
import UserStore from "../lib/user_store";
import messageType from "../constants/message_types";
import { userLogout } from "../actions/user";
import uuid from "uuid/v4";
import get from "lodash/get";
class ActionManager {
  // Override with an error message specific to the action
  // domain this class manages.
  defaultErrorMessage() {
    return "Uh oh, something went wrong. Please try again later.";
  }

  // Override with a function that takes dispatch as an argument
  // and dispatches a start action. If you need to pass
  // arguments to the action creator, override preCall instead.
  defaultPreCallActionCreator() {
    return function() {
      return { type: "NO_ACTION" };
    };
  }

  // Override with a function that takes dispatch as an argument
  // and dispatches a success action. By default, the success
  // action is passed the json object from the response.
  // If you need to pass other stuff from the constructor args,
  // override responseOK().  If you need to do something fancier,
  // you can override run(), but that should be avoided.
  defaultSuccessActionCreator() {
    return function() {
      return { type: "NO_ACTION" };
    };
  }

  // Same as above. Override response400 for customization.
  default400ActionCreator() {
    return function() {
      return { type: "NO_ACTION" };
    };
  }

  // Same as anbove. Override responseError for customization.
  defaultErrorActionCreator() {
    return () => {
      return { type: "NO_ACTION" };
    };
  }

  // preCall allows hooks for emitting actions before the call
  // begins
  preCall(dispatch) {
    dispatch(this.defaultPreCallActionCreator()());
  }

  // Execute the API call.
  execute() {
    // Fill in an API call that returns a promise here.
    return new Promise(resolve => {
      resolve('{"status": "ok"}');
    });
  }

  // responseOK dispatches actions when the call is successful.
  // Override defaultSuccessActionCreator() where possible instead
  // of this. Sometimes you'll need to override this though.
  responseOK(dispatch, json) {
    return dispatch(this.defaultSuccessActionCreator()(json));
  }

  // Same as above, but for 400's.
  response400(dispatch, json) {
    if (json.validationErrors && json.validationErrors.length) {
      return dispatch(this.default400ActionCreator()(json.validationErrors));
    }
    this.responseError(dispatch, 400, json);
  }

  // Same as above but for non-200, non-400, non-403 responses.
  responseError(dispatch, statusCode, json) {
    // Fill in an action creator here to handle specific 500
    // errors if you want something more specific than this.
    var errorMessage = json.errorMessage || json.message || this.defaultErrorMessage();
    dispatch(this.defaultErrorActionCreator()(errorMessage));
    this.showError(dispatch, errorMessage);
  }

  // Handles 403's by logging the user out and emitting a warning.
  response403(dispatch) {
    UserStore.remove();
    dispatch(userLogout());
    this.showWarning(dispatch, "You've been logged out. Please sign back in.");
  }

  // Handles unknown exceptions.  Override for customizations.
  catchError(dispatch) {
    dispatch(this.defaultErrorActionCreator()(this.defaultErrorMessage()));
    this.showError(dispatch, this.defaultErrorMessage());
  }

  // When constructing an action manager, you add
  // you pass all necessary data as an 'args' object.
  // These are stored as this.args and accessible for all
  // other calls.
  constructor(args) {
    this.args = args;
    this.uuid = uuid();
  }

  // Run takes an input object with all necessary
  // data / context for the 3rd party call. It returns
  // a function expecting dispatch and running through
  // the 3rd party call.
  run() {
    var that = this;
    return (dispatch, getState) => {
      that.preCall(dispatch);
      return that
        .execute(dispatch, getState)
        .then(response => {
          let responseData = {
            status: response.status,
            json: {},
          };

          if (response.status === 204) {
            that.responseOK(dispatch, {});
            return responseData;
          }

          return response
            .json()
            .then(json => {
              responseData.json = json;
              switch (response.status) {
                case 200:
                  that.responseOK(dispatch, json);
                  break;
                case 400:
                  that.response400(dispatch, json);
                  break;
                case 403:
                  that.response403(dispatch, json);
                  break;
                default:
                  that.responseError(dispatch, response.status, json);
              }

              return responseData;
            })
            .catch(err => {
              that.catchError(dispatch, err);
              return responseData;
            });
        })
        .catch(err => {
          that.catchError(dispatch, err);
          return {};
        });
    };
  }

  showMessageWithTimeout(dispatch, message, selectedMessageType) {
    if (message && !get(this.args, "silent")) {
      dispatch(showMessageWithTimeout(message, selectedMessageType));
    }
  }
  showError(dispatch, message) {
    this.showMessageWithTimeout(dispatch, message, messageType.Error);
  }
  showWarning(dispatch, message) {
    this.showMessageWithTimeout(dispatch, message, messageType.Warning);
  }
  showInfo(dispatch, message) {
    this.showMessageWithTimeout(dispatch, message, messageType.Info);
  }
  showSuccess(dispatch, message) {
    this.showMessageWithTimeout(dispatch, message, messageType.Success);
  }
}

export default ActionManager;
